/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  Badge,
  HStack,
  Icon,
  Button,
  useToast,
  Spinner,
  Center,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Switch,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Input,
  SimpleGrid,
  Heading,
  Td,                    // ← تم إضافته هنا
} from "@chakra-ui/react";
import Swal from "sweetalert2";

import { FaUserTie, FaPlus, FaSave, FaTrash, FaEdit } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../hooks/useTheme";
import PageContainer from "../../components/PageContainer";
import ResponsiveTable from "../../components/ResponsiveTable"
import { useSuperAdminPermissions } from "../../../hooks/useSuperAdminPermissions";
import NoAccess from "../../components/NoAccess";

export default function Employees() {
  const { t } = useTranslation();
  const { isArabic, colors, dir } = useTheme();
  const toast = useToast();

  const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const perms = useSuperAdminPermissions();

  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");

  const [formData, setFormData] = useState({
    userId: 0,
    userName: "",
    engUserName: "",
    email: "",
    password: "",
    isActive: 1,
    userTypeId: 1,
    userFunctionId: 0,

    viewAdminDashboard: 0,
    viewAdminStores: 0,
    addAdminStore: 0,
    editAdminStore: 0,
    deleteAdminStore: 0,
    viewAdminPayments: 0,
    viewAdminPlansSubscriptions: 0,
    viewAdminReportsRevenues: 0,
    viewAdminReportsPlans: 0,
    viewAdminReportsGrowth: 0,
    viewAdminReportsPayments: 0,
    viewAdminNotifications: 0,
    viewAdminShipping: 0,
    viewAdminPlansPage: 0,
    addAdminPlan: 0,
    editAdminPlan: 0,
    deleteAdminPlan: 0,
    viewAdminEmployees: 0,
    addAdminEmployee: 0,
    editAdminEmployee: 0,
    deleteAdminEmployee: 0,
  });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const baseUrl = API_BASE.replace(/\/+$/, "");

        console.log("📥 Fetching admin employees from:", `${baseUrl}/Users/GetUsers?UserId=-1`);

        const res = await fetch(`${baseUrl}/Users/GetUsers?UserId=-1`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
          },
        });

        console.log("📥 GetUsers response status:", res.status, res.statusText);

        if (!res.ok) {
          const errorText = await res.text();
          console.error("❌ GetUsers failed:", errorText);
          throw new Error("Failed to load users");
        }

        const data = await res.json();
        console.log("📥 GetUsers response data:", data);

        if (Array.isArray(data)) {
          console.log("✅ Loaded", data.length, "admin employees");
          setUsers(data);
        } else {
          console.warn("⚠️ GetUsers response is not an array:", data);
          setUsers([]);
        }
      } catch (err) {
        console.error("❌ GetUsers error:", err);
        toast({
          title: isArabic ? "فشل تحميل الموظفين" : "Failed to load employees",
          description: err.message,
          status: "error",
        });
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [isArabic, toast]);

  const openAddModal = () => {
    setFormData({
      userId: 0,
      userName: "",
      engUserName: "",
      email: "",
      password: "",
      isActive: 1,
      userTypeId: 0, // Always 0 for admin dashboard employees (fixed, not editable)
      userFunctionId: 0, // Always 0 for admin dashboard employees (fixed, not editable)
      viewAdminDashboard: 0,
      viewAdminStores: 0, addAdminStore: 0, editAdminStore: 0, deleteAdminStore: 0,
      viewAdminPayments: 0, viewAdminPlansSubscriptions: 0,
      viewAdminReportsRevenues: 0, viewAdminReportsPlans: 0, viewAdminReportsGrowth: 0, viewAdminReportsPayments: 0,
      viewAdminNotifications: 0, viewAdminShipping: 0,
      viewAdminPlansPage: 0, addAdminPlan: 0, editAdminPlan: 0, deleteAdminPlan: 0,
      viewAdminEmployees: 0, addAdminEmployee: 0, editAdminEmployee: 0, deleteAdminEmployee: 0,
    });
    setFormKey(prev => prev + 1);
    setIsModalOpen(true);
  };

  const openEditModal = (user) => {
    setFormData({
      userId: user.UserId || user.userId || 0,
      userName: user.UserName || "",
      engUserName: user.EngUserName || "",
      email: user.Email || "",
      password: "",
      isActive: user.IsActive ?? 1,
      userTypeId: 0,
      userFunctionId: 0,

      viewAdminDashboard: user.viewAdminDashboard ?? 0,
      viewAdminStores: user.viewAdminStores ?? 0,
      addAdminStore: user.addAdminStore ?? 0,
      editAdminStore: user.editAdminStore ?? 0,
      deleteAdminStore: user.deleteAdminStore ?? 0,
      viewAdminPayments: user.viewAdminPayments ?? 0,
      viewAdminPlansSubscriptions: user.viewAdminPlansSubscriptions ?? 0,
      viewAdminReportsRevenues: user.viewAdminReportsRevenues ?? 0,
      viewAdminReportsPlans: user.viewAdminReportsPlans ?? 0,
      viewAdminReportsGrowth: user.viewAdminReportsGrowth ?? 0,
      viewAdminReportsPayments: user.viewAdminReportsPayments ?? 0,
      viewAdminNotifications: user.viewAdminNotifications ?? 0,
      viewAdminShipping: user.viewAdminShipping ?? 0,
      viewAdminPlansPage: user.viewAdminPlansPage ?? 0,
      addAdminPlan: user.addAdminPlan ?? 0,
      editAdminPlan: user.editAdminPlan ?? 0,
      deleteAdminPlan: user.deleteAdminPlan ?? 0,
      viewAdminEmployees: user.viewAdminEmployees ?? 0,
      addAdminEmployee: user.addAdminEmployee ?? 0,
      editAdminEmployee: user.editAdminEmployee ?? 0,
      deleteAdminEmployee: user.deleteAdminEmployee ?? 0,
    });
    setFormKey(prev => prev + 1);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.userName.trim() || !formData.engUserName.trim()) {
      toast({ title: isArabic ? "الاسم مطلوب" : "Name is required", status: "warning" });
      return;
    }

    if (!formData.email.includes("@")) {
      toast({ title: isArabic ? "بريد إلكتروني غير صحيح" : "Invalid email", status: "warning" });
      return;
    }

    if (formData.userId === 0 && !formData.password) {
      toast({ title: isArabic ? "كلمة المرور مطلوبة" : "Password required", status: "warning" });
      return;
    }

    const formatDate = d =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    try {
      setSaving(true);

      const token = localStorage.getItem("authToken");
      const baseUrl = API_BASE.replace(/\/+$/, "");

      const isEditMode = formData.userId > 0;
      const userId = isEditMode ? formData.userId : 0;

      const payload = {
        userId,
        userName: formData.userName.trim(),
        engUserName: formData.engUserName.trim(),
        email: formData.email.trim(),
        password: formData.password || "",
        isActive: formData.isActive,
        isDeleted: 0,
        creationDate: formatDate(new Date()),
        userTypeId: 0,
        userFunctionId: 0,
        viewAdminDashboard: formData.viewAdminDashboard,
        viewAdminStores: formData.viewAdminStores,
        addAdminStore: formData.addAdminStore,
        editAdminStore: formData.editAdminStore,
        deleteAdminStore: formData.deleteAdminStore,
        viewAdminPayments: formData.viewAdminPayments,
        viewAdminPlansSubscriptions: formData.viewAdminPlansSubscriptions,
        viewAdminReportsRevenues: formData.viewAdminReportsRevenues,
        viewAdminReportsPlans: formData.viewAdminReportsPlans,
        viewAdminReportsGrowth: formData.viewAdminReportsGrowth,
        viewAdminReportsPayments: formData.viewAdminReportsPayments,
        viewAdminNotifications: formData.viewAdminNotifications,
        viewAdminShipping: formData.viewAdminShipping,
        viewAdminPlansPage: formData.viewAdminPlansPage,
        addAdminPlan: formData.addAdminPlan,
        editAdminPlan: formData.editAdminPlan,
        deleteAdminPlan: formData.deleteAdminPlan,
        viewAdminEmployees: formData.viewAdminEmployees,
        addAdminEmployee: formData.addAdminEmployee,
        editAdminEmployee: formData.editAdminEmployee,
        deleteAdminEmployee: formData.deleteAdminEmployee,
      };

      const endpoint = isEditMode
        ? `${baseUrl}/Users/EditUser?UserId=${userId}`
        : `${baseUrl}/Users/AddUser`;

      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(await res.text());

      toast({ title: isArabic ? "تم الحفظ بنجاح" : "Saved successfully", status: "success" });
      setIsModalOpen(false);

      const refresh = await fetch(`${baseUrl}/Users/GetUsers?UserId=-1`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });

      if (refresh.ok) setUsers(await refresh.json());
    } catch (err) {
      toast({ title: "Save failed", description: err.message, status: "error" });
    } finally {
      setSaving(false);
    }
  };


  const handleDelete = async (userId) => {
    const result = await Swal.fire({
      title: isArabic ? "تأكيد الحذف" : "Confirm Delete",
      text: isArabic
        ? "هل أنت متأكد أنك تريد حذف هذا الموظف؟ لا يمكن التراجع عن العملية."
        : "Are you sure you want to delete this employee? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E53E3E",
      cancelButtonColor: "#A0AEC0",
      confirmButtonText: isArabic ? "نعم، احذف" : "Yes, delete",
      cancelButtonText: isArabic ? "إلغاء" : "Cancel",
      reverseButtons: isArabic,
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem("authToken");
      const baseUrl = API_BASE.replace(/\/+$/, "");

      const res = await fetch(`${baseUrl}/Users/DeleteUsers?userId=${userId}`, {
        method: "DELETE",
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });

      if (!res.ok) throw new Error(await res.text());

      setUsers(prev => prev.filter(u => (u.UserId || u.userId) !== userId));

      Swal.fire({
        icon: "success",
        title: isArabic ? "تم الحذف" : "Deleted",
        text: isArabic ? "تم حذف الموظف بنجاح." : "Employee deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

    } catch (err) {
      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل الحذف" : "Delete Failed",
        text: isArabic ? "حدث خطأ أثناء عملية الحذف." : "An error occurred while deleting.",
      });
    }
  };


  const togglePermission = (key) => {
    setFormData(prev => {
      const updated = { ...prev };
      updated[key] = prev[key] === 1 ? 0 : 1;

      // لو عطّل الـ view لأي قسم
      if (key.startsWith("viewAdmin") && updated[key] === 0) {
        const section = key.replace("viewAdmin", "").toLowerCase();

        // اجمع الصلاحيات التابعة لنفس القسم
        const related = Object.keys(prev).filter(x =>
          x.toLowerCase().includes(section) && x !== key
        );

        related.forEach(r => updated[r] = 0);

        toast({
          title: isArabic ? "تنبيه" : "Notice",
          description: isArabic
            ? "بما أنك عطلت الصلاحية الأساسية، جميع صلاحيات العمليات التابعة تم تعطيلها"
            : "Since you disabled the main view permission, all related CRUD permissions are disabled.",
          status: "warning",
          duration: 4500,
        });
      }

      return updated;
    });
  };


  const toggleSection = (keys, value) => {
    setFormData(prev => {
      const updated = { ...prev };
      keys.forEach(k => updated[k] = value ? 1 : 0);
      return updated;
    });
  };

  if (loading) return <Center minH="80vh"><Spinner size="xl" color={colors.mainColor} /></Center>;

  if (!perms.viewAdminEmployees) {
    return <NoAccess />; // صفحة منع الوصول
  }

  const getName = (u) => isArabic ? (u.UserName || "—") : (u.EngUserName || u.UserName || "—");

  return (
    <PageContainer title={isArabic ? "إدارة الموظفين" : "Employees Management"}>
      <HStack mb={6} justify="flex-end">
        {perms.addAdminEmployee && (
          <Button leftIcon={<FaPlus />} colorScheme="purple" onClick={openAddModal}>
            {isArabic ? "إضافة موظف" : "Add Employee"}
          </Button>
        )}
      </HStack>

      <ResponsiveTable
        columns={[
          { header: isArabic ? "الاسم" : "Name" },
          { header: isArabic ? "البريد" : "Email" },
          { header: isArabic ? "الحالة" : "Status" },
          { header: isArabic ? "الإجراءات" : "Actions" },
        ]}
        data={users}
        searchable={true}
        renderRow={(user) => (
          <>
            <Td>
              <HStack>
                <Icon as={FaUserTie} color={colors.mainColor} />
                <Text fontWeight="medium">{getName(user)}</Text>
              </HStack>
            </Td>
            <Td>{user.Email || "—"}</Td>
            <Td>
              <Badge colorScheme={user.IsActive ? "green" : "red"}>
                {user.IsActive ? (isArabic ? "نشط" : "Active") : (isArabic ? "معطل" : "Inactive")}
              </Badge>
            </Td>
            <Td>
              <HStack spacing={2}>
                {perms.editAdminEmployee && (
                  <Button size="sm" leftIcon={<FaEdit />} colorScheme="blue" onClick={() => openEditModal(user)}>
                    {isArabic ? "تعديل" : "Edit"}

                  </Button>
                )}
                {perms.deleteAdminEmployee && (
                  <Button size="sm" leftIcon={<FaTrash />} colorScheme="red" onClick={() => handleDelete(user.UserId || user.userId)}>
                    {isArabic ? "حذف" : "Delete"}
                  </Button>
                )}
              </HStack>
            </Td>
          </>
        )}
      />

      <Modal key={formKey} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent key={formKey} dir={dir}>
          <ModalHeader key={formKey} pr={isArabic ? "40px" : "8"} pl={isArabic ? "8" : "40px"}>
            {formData.userId === 0 ? (isArabic ? "إضافة موظف جديد" : "Add New Employee") : (isArabic ? "تعديل موظف" : "Edit Employee")}
          </ModalHeader>
          <ModalCloseButton key={formKey} top={4} right={isArabic ? 4 : "auto"} left={isArabic ? "auto" : 4} />
          <ModalBody key={formKey}>
            <VStack spacing={5} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>{isArabic ? "الاسم (عربي)" : "Name (Arabic)"}</FormLabel>
                  <Input
                    value={formData.userName}
                    onChange={e => setFormData({ ...formData, userName: e.target.value })}
                    bg={colors.inputBg}
                    color={colors.textColor}
                    borderColor={colors.inputBorder}
                    _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                    _hover={{ borderColor: colors.inputFocus }}
                    _dark={{ bg: colors.inputBg, color: colors.textColor }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{isArabic ? "الاسم (إنجليزي)" : "Name (English)"}</FormLabel>
                  <Input
                    value={formData.engUserName}
                    onChange={e => setFormData({ ...formData, engUserName: e.target.value })}
                    bg={colors.inputBg}
                    color={colors.textColor}
                    borderColor={colors.inputBorder}
                    _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                    _hover={{ borderColor: colors.inputFocus }}
                    _dark={{ bg: colors.inputBg, color: colors.textColor }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input
                    key={`email-${formKey}`}
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    bg={colors.inputBg}
                    color={colors.textColor}
                    borderColor={colors.inputBorder}
                    _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                    _hover={{ borderColor: colors.inputFocus }}
                    _dark={{ bg: colors.inputBg, color: colors.textColor }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{formData.userId === 0 ? (isArabic ? "كلمة المرور" : "Password") : (isArabic ? "كلمة مرور جديدة (اختياري)" : "New Password (optional)")}</FormLabel>
                  <Input
                    key={`password-${formKey}`}
                    type="password"
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    bg={colors.inputBg}
                    color={colors.textColor}
                    borderColor={colors.inputBorder}
                    _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                    _hover={{ borderColor: colors.inputFocus }}
                    _dark={{ bg: colors.inputBg, color: colors.textColor }}
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl display="flex" alignItems="center" mt={4}>
                <FormLabel mb="0">{isArabic ? "نشط" : "Active"}</FormLabel>
                <Switch isChecked={formData.isActive === 1} onChange={e => setFormData({ ...formData, isActive: e.target.checked ? 1 : 0 })} />
              </FormControl>

              <Divider />

              <Heading size="md" color={colors.textColor}>{isArabic ? "الصلاحيات" : "Permissions"}</Heading>

              <Accordion allowMultiple defaultIndex={[0, 1, 2, 3, 4, 5, 6, 7]}>
                {/* 1. Dashboard */}
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign={isArabic ? "right" : "left"}>{isArabic ? "لوحة التحكم الرئيسية" : "Main Dashboard"}</Box>
                    <Switch size="sm" isChecked={formData.viewAdminDashboard === 1} onChange={e => toggleSection(["viewAdminDashboard"], e.target.checked)} />
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0">{isArabic ? "عرض لوحة التحكم" : "View Dashboard"}</FormLabel>
                      <Switch isChecked={formData.viewAdminDashboard === 1} onChange={() => togglePermission("viewAdminDashboard")} />
                    </FormControl>
                  </AccordionPanel>
                </AccordionItem>

                {/* 2. Stores */}
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign={isArabic ? "right" : "left"}>{isArabic ? "المتاجر" : "Stores"}</Box>
                    <Switch size="sm" isChecked={["viewAdminStores", "addAdminStore", "editAdminStore", "deleteAdminStore"].every(k => formData[k] === 1)}
                      onChange={e => toggleSection(["viewAdminStores", "addAdminStore", "editAdminStore", "deleteAdminStore"], e.target.checked)} />
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0">{isArabic ? "عرض المتاجر" : "View Stores"}</FormLabel>
                      <Switch isChecked={formData.viewAdminStores === 1} onChange={() => togglePermission("viewAdminStores")} />
                    </FormControl>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0" opacity={formData.viewAdminStores === 0 ? 0.5 : 1}>{isArabic ? "إضافة متجر" : "Add Store"}</FormLabel>
                      <Switch isDisabled={formData.viewAdminStores === 0} isChecked={formData.addAdminStore === 1} onChange={() => togglePermission("addAdminStore")} />
                    </FormControl>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0" opacity={formData.viewAdminStores === 0 ? 0.5 : 1}>{isArabic ? "تعديل متجر" : "Edit Store"}</FormLabel>
                      <Switch isDisabled={formData.viewAdminStores === 0} isChecked={formData.editAdminStore === 1} onChange={() => togglePermission("editAdminStore")} />
                    </FormControl>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0" opacity={formData.viewAdminStores === 0 ? 0.5 : 1}>{isArabic ? "حذف متجر" : "Delete Store"}</FormLabel>
                      <Switch isDisabled={formData.viewAdminStores === 0} isChecked={formData.deleteAdminStore === 1} onChange={() => togglePermission("deleteAdminStore")} />
                    </FormControl>
                  </AccordionPanel>
                </AccordionItem>

                {/* 3. Payments & Subscriptions */}
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign={isArabic ? "right" : "left"}>{isArabic ? "المدفوعات والاشتراكات" : "Payments & Subscriptions"}</Box>
                    <Switch size="sm" isChecked={["viewAdminPayments", "viewAdminPlansSubscriptions"].every(k => formData[k] === 1)}
                      onChange={e => toggleSection(["viewAdminPayments", "viewAdminPlansSubscriptions"], e.target.checked)} />
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0">{isArabic ? "عرض المدفوعات" : "View Payments"}</FormLabel>
                      <Switch isChecked={formData.viewAdminPayments === 1} onChange={() => togglePermission("viewAdminPayments")} />
                    </FormControl>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0">{isArabic ? "عرض تقرير الاشتراكات والباقات" : "View Plans & Subscriptions"}</FormLabel>
                      <Switch isChecked={formData.viewAdminPlansSubscriptions === 1} onChange={() => togglePermission("viewAdminPlansSubscriptions")} />
                    </FormControl>
                  </AccordionPanel>
                </AccordionItem>

                {/* 4. Reports */}
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign={isArabic ? "right" : "left"}>{isArabic ? "التقارير" : "Reports"}</Box>
                    <Switch size="sm" isChecked={["viewAdminReportsRevenues", "viewAdminReportsPlans", "viewAdminReportsGrowth", "viewAdminReportsPayments"].every(k => formData[k] === 1)}
                      onChange={e => toggleSection(["viewAdminReportsRevenues", "viewAdminReportsPlans", "viewAdminReportsGrowth", "viewAdminReportsPayments"], e.target.checked)} />
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0">{isArabic ? "تقرير الإيرادات" : "Revenues Report"}</FormLabel>
                      <Switch isChecked={formData.viewAdminReportsRevenues === 1} onChange={() => togglePermission("viewAdminReportsRevenues")} />
                    </FormControl>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0">{isArabic ? "تقرير الباقات" : "Plans Report"}</FormLabel>
                      <Switch isChecked={formData.viewAdminReportsPlans === 1} onChange={() => togglePermission("viewAdminReportsPlans")} />
                    </FormControl>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0">{isArabic ? "تقرير النمو" : "Growth Report"}</FormLabel>
                      <Switch isChecked={formData.viewAdminReportsGrowth === 1} onChange={() => togglePermission("viewAdminReportsGrowth")} />
                    </FormControl>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0">{isArabic ? "تقرير بوابات الدفع" : "Payment Gateways Report"}</FormLabel>
                      <Switch isChecked={formData.viewAdminReportsPayments === 1} onChange={() => togglePermission("viewAdminReportsPayments")} />
                    </FormControl>
                  </AccordionPanel>
                </AccordionItem>

                {/* 5. Notifications */}
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign={isArabic ? "right" : "left"}>{isArabic ? "الإشعارات" : "Notifications"}</Box>
                    <Switch size="sm" isChecked={formData.viewAdminNotifications === 1} onChange={e => toggleSection(["viewAdminNotifications"], e.target.checked)} />
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0">{isArabic ? "عرض صفحة الإشعارات" : "View Notifications"}</FormLabel>
                      <Switch isChecked={formData.viewAdminNotifications === 1} onChange={() => togglePermission("viewAdminNotifications")} />
                    </FormControl>
                  </AccordionPanel>
                </AccordionItem>

                {/* 6. Shipping */}
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign={isArabic ? "right" : "left"}>{isArabic ? "الشحن" : "Shipping"}</Box>
                    <Switch size="sm" isChecked={formData.viewAdminShipping === 1} onChange={e => toggleSection(["viewAdminShipping"], e.target.checked)} />
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0">{isArabic ? "عرض صفحة الشحن" : "View Shipping"}</FormLabel>
                      <Switch isChecked={formData.viewAdminShipping === 1} onChange={() => togglePermission("viewAdminShipping")} />
                    </FormControl>
                  </AccordionPanel>
                </AccordionItem>

                {/* 7. Plans */}
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign={isArabic ? "right" : "left"}>{isArabic ? "إدارة الباقات" : "Plans Management"}</Box>
                    <Switch size="sm" isChecked={["viewAdminPlansPage", "addAdminPlan", "editAdminPlan", "deleteAdminPlan"].every(k => formData[k] === 1)}
                      onChange={e => toggleSection(["viewAdminPlansPage", "addAdminPlan", "editAdminPlan", "deleteAdminPlan"], e.target.checked)} />
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0">{isArabic ? "عرض صفحة الباقات" : "View Plans Page"}</FormLabel>
                      <Switch isChecked={formData.viewAdminPlansPage === 1} onChange={() => togglePermission("viewAdminPlansPage")} />
                    </FormControl>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0" opacity={formData.viewAdminPlansPage === 0 ? 0.5 : 1}>{isArabic ? "إضافة باقة" : "Add Plan"}</FormLabel>
                      <Switch isDisabled={formData.viewAdminPlansPage === 0} isChecked={formData.addAdminPlan === 1} onChange={() => togglePermission("addAdminPlan")} />
                    </FormControl>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0" opacity={formData.viewAdminPlansPage === 0 ? 0.5 : 1}>{isArabic ? "تعديل باقة" : "Edit Plan"}</FormLabel>
                      <Switch isDisabled={formData.viewAdminPlansPage === 0} isChecked={formData.editAdminPlan === 1} onChange={() => togglePermission("editAdminPlan")} />
                    </FormControl>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0" opacity={formData.viewAdminPlansPage === 0 ? 0.5 : 1}>{isArabic ? "حذف باقة" : "Delete Plan"}</FormLabel>
                      <Switch isDisabled={formData.viewAdminPlansPage === 0} isChecked={formData.deleteAdminPlan === 1} onChange={() => togglePermission("deleteAdminPlan")} />
                    </FormControl>
                  </AccordionPanel>
                </AccordionItem>

                {/* 8. Employees */}
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign={isArabic ? "right" : "left"}>{isArabic ? "إدارة الموظفين" : "Employees Management"}</Box>
                    <Switch size="sm" isChecked={["viewAdminEmployees", "addAdminEmployee", "editAdminEmployee", "deleteAdminEmployee"].every(k => formData[k] === 1)}
                      onChange={e => toggleSection(["viewAdminEmployees", "addAdminEmployee", "editAdminEmployee", "deleteAdminEmployee"], e.target.checked)} />
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0">{isArabic ? "عرض صفحة الموظفين" : "View Employees Page"}</FormLabel>
                      <Switch isChecked={formData.viewAdminEmployees === 1} onChange={() => togglePermission("viewAdminEmployees")} />
                    </FormControl>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0" opacity={formData.viewAdminEmployees === 0 ? 0.5 : 1}>{isArabic ? "إضافة موظف" : "Add Employee"}</FormLabel>
                      <Switch isDisabled={formData.viewAdminEmployees === 0} isChecked={formData.addAdminEmployee === 1} onChange={() => togglePermission("addAdminEmployee")} />
                    </FormControl>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0" opacity={formData.viewAdminEmployees === 0 ? 0.5 : 1}>{isArabic ? "تعديل موظف" : "Edit Employee"}</FormLabel>
                      <Switch isDisabled={formData.viewAdminEmployees === 0} isChecked={formData.editAdminEmployee === 1} onChange={() => togglePermission("editAdminEmployee")} />
                    </FormControl>
                    <FormControl display="flex" justifyContent="space-between">
                      <FormLabel mb="0" opacity={formData.viewAdminEmployees === 0 ? 0.5 : 1}>{isArabic ? "حذف موظف" : "Delete Employee"}</FormLabel>
                      <Switch isDisabled={formData.viewAdminEmployees === 0} isChecked={formData.deleteAdminEmployee === 1} onChange={() => togglePermission("deleteAdminEmployee")} />
                    </FormControl>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </VStack>
          </ModalBody>

          <ModalFooter key={formKey} gap={3}>
            <Button
              colorScheme="blue"
              leftIcon={<FaSave />}
              onClick={handleSave}
              isLoading={saving}
              bg={colors.buttonBg}
              color={colors.buttonText}
              _hover={{ bg: colors.buttonHover }}
            >
              {isArabic ? "حفظ التغييرات" : "Save Changes"}
            </Button>
            <Button
              onClick={() => setIsModalOpen(false)}
              variant="ghost"
              color={colors.textColor}
              _hover={{ bg: colors.hoverBg }}
            >
              {isArabic ? "إلغاء" : "Cancel"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageContainer>
  );
}